import { alpha } from "@mui/material/styles";
import {
  MODERN_FOCUS_STATES,
  MODERN_GRADIENTS,
  MODERN_HOVER_EFFECTS,
  MODERN_SHADOWS,
  MODERN_TRANSITIONS,
} from "../styles/modernVariables";
import { ICustomTheme } from "../types/ICustomTheme";

export const getIconButtonStyles = () => ({
  styleOverrides: {
    root: ({ theme }: { theme: any }) => ({
      borderRadius: "2px", // 2px conforme solicitado
      transition: MODERN_TRANSITIONS.smooth,
      position: "relative" as const,
      overflow: "hidden", // Garante que efeitos não vazem
      color:
        theme.palette.mode === "dark"
          ? theme.palette.text.primary // Melhor contraste no dark
          : theme.palette.text.secondary,

      // Garantir que o ícone fique sempre visível
      "& .MuiSvgIcon-root": {
        position: "relative" as const,
        zIndex: 2,
      },

      // Efeito ripple moderno (atrás do conteúdo) - adaptável ao tema
      "&::before": {
        content: '""',
        position: "absolute" as const,
        top: "50%",
        left: "50%",
        width: 0,
        height: 0,
        borderRadius: "50%", // Formato redondo para o efeito de hover
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.15)"
            : "rgba(0, 0, 0, 0.08)",
        transform: "translate(-50%, -50%)",
        transition: MODERN_TRANSITIONS.fast,
        opacity: 0,
        zIndex: -1,
        pointerEvents: "none" as const,
      }, // Desabilitar efeitos padrão do Material-UI e aplicar hover customizado
      "&:hover": {
        backgroundColor: "transparent !important", // Remove qualquer background padrão
        color:
          theme.palette.mode === "dark"
            ? "#FFFFFF" // Branco puro no hover para máximo contraste
            : theme.palette.text.primary,
        transform: "scale(1.3)",
        boxShadow: "none", // Remove a sombra externa
        "&::before": {
          width: "80%", // Reduzido de 120% para 80%
          height: "80%", // Reduzido de 120% para 80%
          opacity: 1,
        },
      },

      "&:active": {
        transform: "translateY(-2px) scale(0.98)",
        boxShadow: MODERN_SHADOWS.elevation[2],
      }, // Focus moderno
      "&:focus-visible": {
        ...MODERN_FOCUS_STATES.ring(theme.palette.primary.main),
      },
    }),
  },
});

export const getCardStyles = (themeOptions: ICustomTheme) => ({
  styleOverrides: {
    root: ({ theme }: { theme: any }) => ({
      borderRadius: "4px", // 4px conforme solicitado
      transition: MODERN_TRANSITIONS.smooth,
      position: "relative" as const,
      overflow: "hidden" as const,

      // Glassmorphism effect
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.background.paper, 0.8)
          : alpha(theme.palette.background.paper, 0.9),
      backdropFilter: "blur(12px)",
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,

      // Gradiente sutil de fundo
      "&::before": {
        content: '""',
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: MODERN_GRADIENTS.subtle(themeOptions),
        opacity: 0,
        transition: MODERN_TRANSITIONS.smooth,
        zIndex: -1,
      },

      "&:hover": {
        ...MODERN_HOVER_EFFECTS.lift,
        borderColor: alpha(theme.palette.primary.main, 0.3),

        "&::before": {
          opacity: 1,
        },
      },

      // Efeito de brilho no topo
      "&::after": {
        content: '""',
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.5)}, transparent)`,
        opacity: 0,
        transition: MODERN_TRANSITIONS.smooth,
      },

      "&:hover::after": {
        opacity: 1,
      },
    }),
  },
});

export const getTypographyStyles = (themeOptions: ICustomTheme) => ({
  styleOverrides: {
    root: {
      fontFamily: themeOptions.typography.fontFamily,
    },
    h1: {
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 500,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
});

export const getInputAdornmentStyles = (themeOptions: ICustomTheme) => ({
  styleOverrides: {
    root: {
      color: themeOptions.palette.text.secondary,
      "& .MuiSvgIcon-root": {
        transition: MODERN_TRANSITIONS.fast,
        color: themeOptions.palette.text.secondary,
        "&:hover": {
          color: themeOptions.palette.text.primary,
          transform: "scale(1.1)",
        },
      },
      "& .MuiIconButton-root": {
        color: themeOptions.palette.text.secondary,
        "&:hover": {
          color: themeOptions.palette.text.primary,
        },
      },
    },
  },
});

export const getTooltipStyles = () => ({
  styleOverrides: {
    tooltip: ({ theme }: { theme: any }) => ({
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.grey[900], 0.95)
          : alpha(theme.palette.grey[800], 0.95),
      color:
        theme.palette.mode === "dark"
          ? theme.palette.common.white
          : theme.palette.common.white,
      fontSize: "0.75rem",
      fontWeight: 500,
      borderRadius: "2px", // 2px conforme solicitado
      padding: "8px 12px",
      backdropFilter: "blur(10px)",
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      boxShadow: MODERN_SHADOWS.elevation[3],

      // Animação de entrada suave
      transition: MODERN_TRANSITIONS.fast,
      transform: "scale(0.9)",
      transformOrigin: "center",

      "&.MuiTooltip-tooltipPlacementTop": {
        marginBottom: "8px",
      },
      "&.MuiTooltip-tooltipPlacementBottom": {
        marginTop: "8px",
      },
      "&.MuiTooltip-tooltipPlacementLeft": {
        marginRight: "8px",
      },
      "&.MuiTooltip-tooltipPlacementRight": {
        marginLeft: "8px",
      },
    }),
    arrow: ({ theme }: { theme: any }) => ({
      color:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.grey[900], 0.95)
          : alpha(theme.palette.grey[800], 0.95),
    }),
  },
  defaultProps: {
    arrow: true,
    enterDelay: 500,
    leaveDelay: 200,
    enterTouchDelay: 700,
    leaveTouchDelay: 1500,
  },
});

export const getChipStyles = () => ({
  styleOverrides: {
    root: ({ theme }: { theme: any }) => ({
      borderRadius: "4px", // 4px conforme solicitado
      transition: MODERN_TRANSITIONS.smooth,
      position: "relative" as const,
      overflow: "hidden" as const,
      cursor: "pointer",
      fontWeight: 500,
      fontSize: "0.875rem",

      // Glassmorphism base
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.background.paper, 0.8)
          : alpha(theme.palette.background.paper, 0.9),
      backdropFilter: "blur(8px)",
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      boxShadow: MODERN_SHADOWS.elevation[1],

      // Gradiente sutil de fundo
      "&::before": {
        content: '""',
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)"
            : "linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.05) 100%)",
        opacity: 0,
        transition: MODERN_TRANSITIONS.smooth,
        zIndex: 0,
        pointerEvents: "none" as const,
      },

      // Garantir que o conteúdo fique visível
      "& .MuiChip-label": {
        position: "relative" as const,
        zIndex: 2,
        color:
          theme.palette.mode === "dark"
            ? theme.palette.text.primary
            : theme.palette.text.secondary,
        transition: MODERN_TRANSITIONS.fast,
      },

      // Efeito de hover moderno
      "&:hover": {
        ...MODERN_HOVER_EFFECTS.lift,
        borderColor: alpha(theme.palette.primary.main, 0.4),
        boxShadow: MODERN_SHADOWS.glow.primary(theme.palette.primary.main),

        "&::before": {
          opacity: 1,
        },

        "& .MuiChip-label": {
          color:
            theme.palette.mode === "dark"
              ? "#FFFFFF"
              : theme.palette.text.primary,
          fontWeight: 600,
        },
      },

      // Estado ativo/pressionado
      "&:active": {
        transform: "translateY(-2px) scale(0.98)",
        boxShadow: MODERN_SHADOWS.elevation[2],
      },

      // Focus moderno
      "&:focus-visible": {
        ...MODERN_FOCUS_STATES.ring(theme.palette.primary.main),
      },

      // Chip selecionado (clickable)
      "&.MuiChip-clickable": {
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
      },

      // Variações de cor - Primary
      "&.MuiChip-colorPrimary": {
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        color: theme.palette.primary.main,
        borderColor: alpha(theme.palette.primary.main, 0.3),

        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
          color:
            theme.palette.mode === "dark"
              ? "#FFFFFF"
              : theme.palette.primary.dark,
          borderColor: theme.palette.primary.main,
          boxShadow: MODERN_SHADOWS.glow.primary(theme.palette.primary.main),
        },
        // Estado selecionado/filled
        "&.MuiChip-filled": {
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "#FFFFFF", // Sempre branco para máximo contraste
          borderColor: theme.palette.primary.dark,

          "& .MuiChip-label": {
            color: "#FFFFFF !important", // Força texto branco em chips selecionados
            fontWeight: 600,
          },

          // Indicador visual de seleção
          "&::after": {
            content: '""',
            position: "absolute" as const,
            top: 4,
            right: 4,
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "#FFFFFF", // Sempre branco para o indicador
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
            animation: "chipSelected 0.6s ease-out",
            zIndex: 3,
          },

          "&:hover": {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            transform: "translateY(-3px) scale(1.02)",
            boxShadow: MODERN_SHADOWS.glow.primary(theme.palette.primary.main),
          },
        },
      },

      // Variações de cor - Secondary
      "&.MuiChip-colorSecondary": {
        backgroundColor: alpha(theme.palette.secondary.main, 0.12),
        color: theme.palette.secondary.main,
        borderColor: alpha(theme.palette.secondary.main, 0.3),

        "&:hover": {
          backgroundColor: alpha(theme.palette.secondary.main, 0.2),
          color:
            theme.palette.mode === "dark"
              ? "#FFFFFF"
              : theme.palette.secondary.dark,
          borderColor: theme.palette.secondary.main,
          boxShadow: MODERN_SHADOWS.glow.secondary(
            theme.palette.secondary.main,
          ),
        },
        "&.MuiChip-filled": {
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: "#FFFFFF", // Sempre branco para máximo contraste
          borderColor: theme.palette.secondary.dark,

          "& .MuiChip-label": {
            color: "#FFFFFF !important", // Força texto branco em chips selecionados
            fontWeight: 600,
          },

          "&::after": {
            content: '""',
            position: "absolute" as const,
            top: 4,
            right: 4,
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "#FFFFFF", // Sempre branco para o indicador
            boxShadow: `0 0 0 1px ${theme.palette.secondary.main}`,
            animation: "chipSelected 0.6s ease-out",
            zIndex: 3,
          },

          "&:hover": {
            background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
            transform: "translateY(-3px) scale(1.02)",
            boxShadow: MODERN_SHADOWS.glow.secondary(
              theme.palette.secondary.main,
            ),
          },
        },
      },

      // Chip deletável
      "& .MuiChip-deleteIcon": {
        position: "relative" as const,
        zIndex: 2,
        transition: MODERN_TRANSITIONS.fast,
        color:
          theme.palette.mode === "dark"
            ? theme.palette.text.secondary
            : theme.palette.text.secondary,

        "&:hover": {
          color: theme.palette.error.main,
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          borderRadius: "50%",
          transform: "scale(1.1)",
        },
      },

      // Removendo animação automática que causava animações indesejadas em re-renders

      // Keyframes para animações (mantidos para uso manual se necessário)
      "@keyframes chipFadeIn": {
        "0%": {
          opacity: 0,
          transform: "scale(0.8) translateY(10px)",
        },
        "100%": {
          opacity: 1,
          transform: "scale(1) translateY(0)",
        },
      },

      "@keyframes chipSelected": {
        "0%": {
          transform: "scale(0)",
          opacity: 0,
        },
        "50%": {
          transform: "scale(1.2)",
          opacity: 1,
        },
        "100%": {
          transform: "scale(1)",
          opacity: 1,
        },
      },
    }),

    // Estilos para o avatar
    avatar: ({ theme }: { theme: any }) => ({
      width: 24,
      height: 24,
      fontSize: "0.75rem",
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      transition: MODERN_TRANSITIONS.fast,
    }),

    // Estilos para o ícone
    icon: ({ theme: _ }: { theme: any }) => ({
      fontSize: "1rem",
      color: "inherit",
      transition: MODERN_TRANSITIONS.fast,
    }),
  },

  defaultProps: {
    variant: "outlined" as const,
  },
});

export const getPaperStyles = () => ({
  styleOverrides: {
    root: ({ theme }: { theme: any }) => ({
      borderRadius: "2px", // 2px conforme solicitado
      transition: MODERN_TRANSITIONS.smooth,
      position: "relative" as const,

      // Glassmorphism effect sutil para Papers
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.background.paper, 0.85)
          : alpha(theme.palette.background.paper, 0.95),
      backdropFilter: "blur(8px)",
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

      // Hover sutil para papers interativos
      "&:hover": {
        borderColor: alpha(theme.palette.divider, 0.2),
      },
    }),
  },
});
